// Attachment chips + attach button for the dashboard composer's prompt.
//
// Two exports rather than one component, because the two halves live in
// different places: the chips go ABOVE the textarea (they are inputs to the
// prompt, and putting them below shoves the controls row around as each upload's
// chip arrives) while the + button sits IN the controls row beside the unit
// select and Send.
//
// Presentational: the host owns the list and does the uploading, because the
// upload has to be in flight before Send is pressed (a 200 MB STEP would
// otherwise block the click) and the host is what gates Send on it.
//
// Modelled on BugReportButton's dropzone — the only drag-drop in the app. It has
// a window-level paste listener too; this deliberately does NOT. A screenshot is
// pasteable, a STEP file is not, so a paste hook here would only ever intercept
// text the user meant for the textarea.

import { useRef, useState } from 'react';
import { fileIcon, formatSize } from './FileRepository';
import { ATTACH_ACCEPT, CAD_ACCEPT, GCODE_ACCEPT } from '../constants/attachments';
import './PromptAttachments.css';

/**
 * The chip row. Renders nothing when there is nothing attached.
 *
 * @param files    [{ localId, fileName, fileSize, status: 'uploading'|'ready'|'error',
 *                    progress, error }]
 * @param onRemove (localId) => void
 */
export default function PromptAttachments({ files = [], onRemove }) {
  if (!files.length) return null;
  return (
    <div className="attach-row">
      {files.map((f) => (
        <span
          key={f.localId}
          className={`attach-chip${f.status === 'error' ? ' error' : ''}`}
          title={f.status === 'error' ? f.error : f.fileName}
        >
          <span className="attach-chip-icon">{fileIcon(null, f.fileName)}</span>
          <span className="attach-chip-name">{f.fileName}</span>
          <span className="attach-chip-meta">
            {f.status === 'uploading'
              ? `${f.progress ?? 0}%`
              : f.status === 'error'
                ? 'failed'
                : formatSize(f.fileSize)}
          </span>
          <button
            type="button"
            className="attach-chip-remove"
            aria-label={`Remove ${f.fileName}`}
            onClick={() => onRemove(f.localId)}
          >
            ✕
          </button>
          {f.status === 'uploading' && (
            <span className="attach-chip-bar" style={{ width: `${f.progress ?? 0}%` }} />
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * The + button, its kind menu, and the hidden input.
 *
 * The + opens a two-item menu rather than the picker directly: the composer
 * takes two unrelated kinds of file, and an OS dialog listing all seven
 * extensions at once makes the user do the sorting. Picking a kind narrows the
 * dialog to just that kind's extensions.
 *
 * Note what this is NOT: `accept` is a filter on the dialog, not a gate. Any
 * dialog offers an "all files" escape and a drag-drop skips the button
 * entirely, so `classifyAttachment` in the host stays the thing that actually
 * refuses a file — picking "CAD" and forcing a .gcode through still uploads it
 * as a toolpath, correctly.
 *
 * The third item is a different ACTION, not a third kind: the two above it
 * upload a file from disk (creating a repository record), while "Browse
 * repository" attaches one that is already there by reference. Hence the
 * separator — and hence it offers no kind sub-choice, since the picker filters
 * by kind itself and a file already in the repository can be found by name.
 *
 * @param onAdd    (FileList | File[]) => void
 * @param onBrowse () => void — open the existing-file picker. Optional; with no
 *                 handler the third item is not offered, so a caller that only
 *                 uploads is unchanged.
 */
export function AttachButton({ onAdd, onBrowse, disabled }) {
  const inputRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // The accept filter is applied when the dialog OPENS, so it has to be on the
  // DOM node before .click(). Writing it imperatively rather than through state
  // sidesteps that ordering entirely — a setState here would not have committed
  // by the time the synchronous click ran. The hidden input is already driven
  // imperatively (.click(), .value = ''), so this is the file's existing idiom.
  function pick(accept) {
    setMenuOpen(false);
    const el = inputRef.current;
    if (!el) return;
    el.accept = accept;
    el.click();
  }

  return (
    <div
      className="attach-wrap"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && menuOpen) {
          e.stopPropagation();
          setMenuOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="attach-btn"
        onClick={() => setMenuOpen((v) => !v)}
        disabled={disabled}
        title="Add a CAD or G-code file"
        aria-label="Add a file"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        +
      </button>
      {menuOpen && (
        <>
          {/* Same backdrop idiom as Dashboard's ⋯ menus — a click anywhere else
              closes, without a document-level listener to tear down. */}
          <div className="attach-menu-backdrop" onClick={() => setMenuOpen(false)} />
          {/* Its own name, not the trigger's — two elements sharing "Add a file"
              makes an accessible-name lookup ambiguous while the menu is open. */}
          <div className="attach-menu" role="menu" aria-label="Attach a file">
            <button type="button" role="menuitem" onClick={() => pick(CAD_ACCEPT)}>
              Add a CAD file
            </button>
            <button type="button" role="menuitem" onClick={() => pick(GCODE_ACCEPT)}>
              Add a G-code file
            </button>
            {onBrowse && (
              <>
                <div className="attach-menu-sep" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onBrowse();
                  }}
                >
                  Browse repository…
                </button>
              </>
            )}
          </div>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        // A named control rather than an anonymous one: it is hidden and driven
        // by the menu above, but a file input with no accessible name is still a
        // file input with no accessible name.
        aria-label="Choose a CAD or G-code file"
        accept={ATTACH_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          onAdd(e.target.files);
          // Reset so re-picking the same file fires onChange again.
          e.target.value = '';
          // Back to both kinds, so a drag-drop or a later programmatic open is
          // never narrowed by whichever menu item was used last.
          e.target.accept = ATTACH_ACCEPT;
        }}
      />
    </div>
  );
}
